import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { INDUSTRIES, COMPANY_TYPES, COMPANY_SIZES } from '../lib/constants';
import {
  Building2, Mail, Lock, Phone, Globe,
  ChevronRight, ChevronLeft,
  CheckCircle, Eye, EyeOff, AlertCircle,
  FileText
} from 'lucide-react';

const STEPS = ['Company Info', 'Contact & Legal', 'Account Setup'];

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: '', industry: '', company_type: '',
    company_size: '', founded_year: '',
    website: '', linkedin_url: '', description: '',
    country: '', city: '', address: '',
    contact_name: '', contact_email: '',
    contact_phone: '', business_reg_number: '',
    tax_id: '',
    email: '', password: '', confirmPassword: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateStep = () => {
    setError('');

    if (step === 0) {
      if (!form.name.trim()) {
        setError('Company name is required');
        return false;
      }
      if (!form.industry) {
        setError('Industry is required');
        return false;
      }
      if (!form.company_type) {
        setError('Company type is required');
        return false;
      }
      if (!form.company_size) {
        setError('Company size is required');
        return false;
      }
      if (!form.description || form.description.length < 50) {
        setError('Description must be at least 50 characters');
        return false;
      }
      if (!form.country) {
        setError('Country is required');
        return false;
      }
      if (!form.city) {
        setError('City is required');
        return false;
      }
    }

    if (step === 1) {
      if (!form.contact_name.trim()) {
        setError('Contact name is required');
        return false;
      }
      if (!form.contact_email.trim()) {
        setError('Contact email is required');
        return false;
      }
      const freeEmails = ['gmail', 'yahoo', 'hotmail', 'outlook'];
      if (freeEmails.some(e => form.contact_email.includes(e))) {
        setError('Please use a business email address');
        return false;
      }
      if (!form.contact_phone.trim()) {
        setError('Contact phone is required');
        return false;
      }
      if (!form.business_reg_number.trim()) {
        setError('Business registration number is required');
        return false;
      }
      if (!certFile) {
        setError('Registration document is mandatory — please upload it');
        return false;
      }
    }

    if (step === 2) {
      if (!form.email.trim()) {
        setError('Login email is required');
        return false;
      }
      if (!form.password || form.password.length < 8) {
        setError('Password must be at least 8 characters');
        return false;
      }
      if (form.password !== form.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setError('');
    try {
      // 1. Create Supabase auth user
      const { data: authData, error: authErr } =
        await supabase.auth.signUp({
          email: form.email.trim(),
          password: form.password,
          options: { data: { role: 'company' } }
        });
      if (authErr) throw authErr;

      const uid = authData.user?.id;
      if (!uid) throw new Error('Account creation failed. Try again.');

      // 2. Upload registration document (mandatory)
      let certUrl = null;
      try {
        const ext = certFile.name.split('.').pop();
        const path = `${uid}/reg_doc.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from('avatars')
          .upload(path, certFile, {
            upsert: true,
            contentType: certFile.type,
          });
        if (!uploadErr) {
          certUrl = supabase.storage
            .from('avatars')
            .getPublicUrl(path).data.publicUrl;
        }
      } catch (uploadEx) {
        console.error('Doc upload failed:', uploadEx);
      }

      // 3. Create company record
      const slug = `${form.name.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')}-${Date.now()}`;

      const insertData = {
        auth_user_id: uid,
        name: form.name.trim(),
        slug,
        industry: form.industry,
        company_type: form.company_type,
        company_size: form.company_size,
        founded_year: form.founded_year
          ? parseInt(form.founded_year) : null,
        website: form.website.trim() || null,
        linkedin_url: form.linkedin_url.trim() || null,
        description: form.description.trim(),
        country: form.country.trim(),
        city: form.city.trim(),
        address: form.address?.trim() || null,
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
        business_reg_number: form.business_reg_number.trim(),
        tax_id: form.tax_id?.trim() || null,
        reg_certificate_url: certUrl,
        plan: 'trial',
        plan_started_at: new Date().toISOString(),
        plan_expires_at: new Date(
          Date.now() + 14 * 24 * 60 * 60 * 1000
        ).toISOString(),
        slots_total: 1,
        slots_used: 0,
        verification_status: 'pending',
        is_active: true,
        is_verified: false,
        is_featured: false,
        account_deleted: false,
      };

      const { error: companyErr } = await supabase
        .from('companies')
        .insert(insertData);

      if (companyErr) {
        if (companyErr.message.includes('contact_email')) {
          throw new Error(
            'This business email is already registered. Please login instead.'
          );
        }
        if (companyErr.message.includes('slug')) {
          // Slug collision — retry with random suffix
          const { error: retryErr } = await supabase
            .from('companies')
            .insert({
              ...insertData,
              slug: `${slug}-${Math.random()
                .toString(36).slice(2, 7)}`,
            });
          if (retryErr) throw retryErr;
        } else {
          throw companyErr;
        }
      }

      setDone(true);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (done) {
    return (
      <div style={pageStyle}>
        <div style={{ width: '100%', maxWidth: 480 }}>
          <LogoHeader />
          <div style={cardStyle}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'linear-gradient(135deg,rgba(79,142,247,0.15),rgba(124,58,237,0.1))',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 20px',
              border: '2px solid rgba(79,142,247,0.25)',
              fontSize: 36,
            }}>🎉</div>
            <h2 style={{
              fontSize: 22, fontWeight: 800,
              color: '#1E293B', marginBottom: 10,
              textAlign: 'center',
            }}>Application Submitted!</h2>
            <p style={{
              color: '#64748B', fontSize: 14,
              lineHeight: 1.7, marginBottom: 20,
              textAlign: 'center',
            }}>
              Your company <strong>{form.name}</strong> has been
              registered successfully. Your application is now
              under review by our admin team.
            </p>
            {[
              { icon: '🔍', text: 'Admin reviews your company information and documents' },
              { icon: '✅', text: 'Once approved, your dashboard is fully unlocked' },
              { icon: '❌', text: 'If rejected, you\'ll see the reason and can reapply' },
              { icon: '💼', text: 'Post jobs and find top students from 50+ countries' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', gap: 12,
                padding: '10px 0',
                borderBottom: i < 3 ? '1px solid #F1F5F9' : 'none',
                alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                <span style={{ fontSize: 13, color: '#64748B', lineHeight: 1.5 }}>
                  {item.text}
                </span>
              </div>
            ))}
            <div style={{
              background: '#EFF6FF', border: '1px solid #BFDBFE',
              borderRadius: 10, padding: '12px 14px',
              fontSize: 13, color: '#1D4ED8',
              lineHeight: 1.6, marginTop: 16, marginBottom: 20,
            }}>
              💡 You can login now and view your pending dashboard
              while we review your application.
            </div>
            <button
              onClick={() => navigate('/login')}
              style={gradBtnStyle}>
              Login to Dashboard →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={{ width: '100%', maxWidth: 580 }}>
        <LogoHeader />
        <div style={cardStyle}>
          {/* Progress */}
          <div style={{ marginBottom: 28 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              marginBottom: 14,
            }}>
              {STEPS.map((s, i) => (
                <div key={i} style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: 6, flex: 1,
                }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: i < step
                      ? 'linear-gradient(135deg,#10B981,#059669)'
                      : i === step
                        ? 'linear-gradient(135deg,#4F8EF7,#7C3AED)'
                        : '#F1F5F9',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                    color: i <= step ? '#fff' : '#94A3B8',
                    fontWeight: 700, fontSize: 13,
                    boxShadow: i <= step
                      ? '0 4px 12px rgba(79,142,247,0.3)' : 'none',
                    transition: 'all 0.25s',
                  }}>
                    {i < step ? <CheckCircle size={16} /> : i + 1}
                  </div>
                  <span style={{
                    fontSize: 11,
                    color: i <= step ? '#4F8EF7' : '#94A3B8',
                    fontWeight: i === step ? 700 : 400,
                    textAlign: 'center',
                  }}>{s}</span>
                </div>
              ))}
            </div>
            <div style={{
              height: 4, background: '#F1F5F9',
              borderRadius: 2, overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${((step + 1) / STEPS.length) * 100}%`,
                background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                transition: 'width 0.35s ease',
                borderRadius: 2,
              }} />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={errorBoxStyle}>
              <AlertCircle size={15} />
              {error}
            </div>
          )}

          {/* Step 0 */}
          {step === 0 && (
            <div style={formColStyle}>
              <StepTitle title="Company Information"
                sub="Tell us about your company" />
              <FInput label="Company Name *" value={form.name}
                onChange={v => set('name', v)}
                placeholder="Acme Corp"
                icon={<Building2 size={16} />} />
              <Row2>
                <FSelect label="Industry *" value={form.industry}
                  onChange={v => set('industry', v)}
                  options={INDUSTRIES} />
                <FSelect label="Company Type *" value={form.company_type}
                  onChange={v => set('company_type', v)}
                  options={COMPANY_TYPES} />
              </Row2>
              <Row2>
                <FSelect label="Company Size *" value={form.company_size}
                  onChange={v => set('company_size', v)}
                  options={COMPANY_SIZES} />
                <FInput label="Founded Year"
                  value={form.founded_year}
                  onChange={v => set('founded_year', v)}
                  placeholder="2020" type="number" />
              </Row2>
              <Row2>
                <FInput label="Country *" value={form.country}
                  onChange={v => set('country', v)}
                  placeholder="Pakistan" />
                <FInput label="City *" value={form.city}
                  onChange={v => set('city', v)}
                  placeholder="Lahore" />
              </Row2>
              <FInput label="Website"
                value={form.website}
                onChange={v => set('website', v)}
                placeholder="https://yourcompany.com"
                icon={<Globe size={16} />} />
              <FInput label="LinkedIn Page"
                value={form.linkedin_url}
                onChange={v => set('linkedin_url', v)}
                placeholder="https://linkedin.com/company/..." />
              <FTextarea
                label="Company Description * (min 50 chars)"
                value={form.description}
                onChange={v => set('description', v)}
                placeholder="Tell students about your company, culture, and what you do..."
                rows={4} />
            </div>
          )}

          {/* Step 1 */}
          {step === 1 && (
            <div style={formColStyle}>
              <StepTitle title="Contact & Legal"
                sub="Used for verification and communication" />
              <FInput label="Contact Person Name *"
                value={form.contact_name}
                onChange={v => set('contact_name', v)}
                placeholder="Muhammad Ali" />
              <FInput label="Business Email * (no gmail/yahoo)"
                value={form.contact_email}
                onChange={v => set('contact_email', v)}
                placeholder="hr@yourcompany.com"
                icon={<Mail size={16} />} type="email" />
              <FInput label="Phone Number *"
                value={form.contact_phone}
                onChange={v => set('contact_phone', v)}
                placeholder="+92 300 1234567"
                icon={<Phone size={16} />} />
              <FInput label="Business Registration Number *"
                value={form.business_reg_number}
                onChange={v => set('business_reg_number', v)}
                placeholder="e.g. 0012345" />
              <FInput label="Tax ID / NTN (optional)"
                value={form.tax_id}
                onChange={v => set('tax_id', v)}
                placeholder="Optional" />

              {/* Document upload — MANDATORY */}
              <div>
                <label style={labelStyle}>
                  Company Registration Document *
                  <span style={{
                    color: '#EF4444', marginLeft: 4,
                    fontSize: 11,
                  }}>Required</span>
                </label>
                <label style={{
                  display: 'flex', alignItems: 'center',
                  gap: 12, padding: '14px 16px',
                  border: certFile
                    ? '2px solid #10B981'
                    : '2px dashed #E2E8F0',
                  borderRadius: 12, cursor: 'pointer',
                  background: certFile
                    ? 'rgba(16,185,129,0.04)' : '#F8FAFF',
                  transition: 'all 0.15s',
                }}>
                  {certFile
                    ? <CheckCircle size={20} color="#10B981" />
                    : <FileText size={20} color="#4F8EF7" />}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: certFile ? '#10B981' : '#475569',
                    }}>
                      {certFile ? certFile.name : 'Upload PDF or Image'}
                    </div>
                    <div style={{
                      fontSize: 11, color: '#94A3B8',
                      marginTop: 2,
                    }}>
                      {certFile
                        ? `${(certFile.size / 1024 / 1024).toFixed(2)} MB`
                        : 'Business registration certificate — PDF, JPG, PNG (max 10MB)'}
                    </div>
                  </div>
                  <input type="file"
                    accept=".pdf,image/*"
                    style={{ display: 'none' }}
                    onChange={e => setCertFile(e.target.files?.[0] || null)} />
                </label>
              </div>

              <div style={{
                background: '#F0F9FF',
                border: '1px solid #BAE6FD',
                borderRadius: 10, padding: '12px 14px',
                fontSize: 12, color: '#0369A1', lineHeight: 1.6,
              }}>
                📋 Your application will be reviewed by our admin team.
                You can login and view your dashboard while we review.
                Approval typically takes 24–48 hours.
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div style={formColStyle}>
              <StepTitle title="Create Your Account"
                sub="Set up your login credentials" />
              <FInput label="Login Email *"
                value={form.email}
                onChange={v => set('email', v)}
                placeholder="you@company.com"
                icon={<Mail size={16} />} type="email" />
              <div style={{ position: 'relative' }}>
                <FInput label="Password * (min 8 chars)"
                  value={form.password}
                  onChange={v => set('password', v)}
                  placeholder="••••••••"
                  icon={<Lock size={16} />}
                  type={showPass ? 'text' : 'password'} />
                <button type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{
                    position: 'absolute', right: 12, top: 34,
                    background: 'none', border: 'none',
                    cursor: 'pointer', color: '#94A3B8',
                  }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <FInput label="Confirm Password *"
                value={form.confirmPassword}
                onChange={v => set('confirmPassword', v)}
                placeholder="••••••••"
                icon={<Lock size={16} />}
                type={showPass ? 'text' : 'password'} />

              {/* Summary */}
              <div style={{
                background: '#F8FAFF',
                border: '1px solid #E2E8F0',
                borderRadius: 12, padding: 16,
              }}>
                <div style={{
                  fontWeight: 700, fontSize: 13,
                  color: '#1E293B', marginBottom: 10,
                }}>Registration Summary</div>
                {[
                  ['Company', form.name],
                  ['Industry', form.industry],
                  ['Location', `${form.city}, ${form.country}`],
                  ['Contact', form.contact_name],
                  ['Business Reg', form.business_reg_number],
                  ['Document', certFile ? `✓ ${certFile.name}` : '—'],
                  ['Plan', 'Free Trial (14 days, 1 slot)'],
                ].map(([k, v]) => (
                  <div key={k} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12, padding: '4px 0',
                    borderBottom: '1px solid #F1F5F9',
                  }}>
                    <span style={{ color: '#94A3B8' }}>{k}</span>
                    <span style={{
                      color: '#1E293B', fontWeight: 600,
                      maxWidth: 240, textAlign: 'right',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            {step > 0 && (
              <button onClick={() => { setError(''); setStep(s => s - 1); }}
                style={backBtnStyle}>
                <ChevronLeft size={16} />Back
              </button>
            )}
            <button
              onClick={step < STEPS.length - 1
                ? () => { if (validateStep()) setStep(s => s + 1); }
                : handleSubmit}
              disabled={loading}
              style={{
                ...gradBtnStyle,
                flex: 2,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}>
              {loading
                ? <Spinner />
                : step < STEPS.length - 1
                  ? <>Continue <ChevronRight size={16} /></>
                  : <>Create Account <CheckCircle size={16} /></>}
            </button>
          </div>

          <p style={{
            textAlign: 'center', fontSize: 13,
            color: '#94A3B8', marginTop: 16,
          }}>
            Already have an account?{' '}
            <Link to="/login" style={{
              color: '#4F8EF7', fontWeight: 600,
            }}>Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────
function LogoHeader() {
  return (
    <div style={{ textAlign: 'center', marginBottom: 28 }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center',
        gap: 10, marginBottom: 6,
      }}>
        <div style={{
          width: 42, height: 42,
          background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
          borderRadius: 12, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 900, fontSize: 20,
        }}>U</div>
        <span style={{
          fontWeight: 800, fontSize: 20,
          background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: 1,
        }}>UNIVERSE Careers</span>
      </div>
      <p style={{ color: '#94A3B8', fontSize: 13 }}>
        Company Portal
      </p>
    </div>
  );
}

function StepTitle({ title, sub }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <h2 style={{
        fontSize: 18, fontWeight: 800, color: '#1E293B',
      }}>{title}</h2>
      <p style={{ fontSize: 13, color: '#94A3B8', marginTop: 3 }}>
        {sub}
      </p>
    </div>
  );
}

function Row2({ children }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr', gap: 12,
    }}>{children}</div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: '#475569', marginBottom: 6, letterSpacing: 0.3,
};

function FInput({ label, value, onChange, placeholder,
  icon, type = 'text' }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        {icon && (
          <div style={{
            position: 'absolute', left: 12,
            top: '50%', transform: 'translateY(-50%)',
            color: '#94A3B8', pointerEvents: 'none',
          }}>{icon}</div>
        )}
        <input type={type} value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: icon ? '11px 14px 11px 38px' : '11px 14px',
            border: '1.5px solid #E2E8F0',
            borderRadius: 10, fontSize: 14,
            color: '#1E293B', background: '#F8FAFF',
            transition: 'border-color 0.15s',
            outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = '#4F8EF7'}
          onBlur={e => e.target.style.borderColor = '#E2E8F0'}
        />
      </div>
    </div>
  );
}

function FSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <select value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', padding: '11px 14px',
          border: '1.5px solid #E2E8F0',
          borderRadius: 10, fontSize: 14,
          color: value ? '#1E293B' : '#94A3B8',
          background: '#F8FAFF', cursor: 'pointer',
          outline: 'none',
        }}
        onFocus={e => e.target.style.borderColor = '#4F8EF7'}
        onBlur={e => e.target.style.borderColor = '#E2E8F0'}
      >
        <option value="">Select...</option>
        {options.map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

function FTextarea({ label, value, onChange,
  placeholder, rows = 3 }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <textarea value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder} rows={rows}
        style={{
          width: '100%', padding: '11px 14px',
          border: '1.5px solid #E2E8F0',
          borderRadius: 10, fontSize: 14,
          color: '#1E293B', background: '#F8FAFF',
          resize: 'vertical', lineHeight: 1.6,
          outline: 'none',
        }}
        onFocus={e => e.target.style.borderColor = '#4F8EF7'}
        onBlur={e => e.target.style.borderColor = '#E2E8F0'}
      />
    </div>
  );
}

function Spinner() {
  return (
    <div style={{
      width: 18, height: 18,
      border: '2px solid rgba(255,255,255,0.4)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
  );
}

// Styles
const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg,#F8FAFF 0%,#EEF2FF 100%)',
  display: 'flex', alignItems: 'center',
  justifyContent: 'center', padding: '24px',
};
const cardStyle = {
  background: '#fff', borderRadius: 20, padding: 36,
  boxShadow: '0 4px 40px rgba(79,142,247,0.08)',
  border: '1px solid #E2E8F0',
};
const formColStyle = {
  display: 'flex', flexDirection: 'column', gap: 14,
};
const errorBoxStyle = {
  background: '#FFF1F2', border: '1px solid #FECDD3',
  borderRadius: 10, padding: '10px 14px',
  marginBottom: 16, display: 'flex',
  alignItems: 'center', gap: 8,
  color: '#E11D48', fontSize: 13,
};
const gradBtnStyle = {
  display: 'flex', alignItems: 'center',
  justifyContent: 'center', gap: 8,
  padding: '13px 24px', border: 'none',
  borderRadius: 12,
  background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
  color: '#fff', fontWeight: 700, fontSize: 14,
  cursor: 'pointer',
  boxShadow: '0 4px 16px rgba(79,142,247,0.3)',
  flex: 2,
};
const backBtnStyle = {
  display: 'flex', alignItems: 'center',
  justifyContent: 'center', gap: 6,
  padding: '13px 20px',
  border: '1px solid #E2E8F0',
  borderRadius: 12, background: '#fff',
  color: '#64748B', fontWeight: 600,
  fontSize: 14, cursor: 'pointer', flex: 1,
};